import { useState, useMemo, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  FlatList,
  Image,
} from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { getRankings, getDogs } from "../../lib/api";
import { Ionicons } from "@expo/vector-icons";
import { AKC_BREEDS } from "../../lib/breeds";
import { hasSizeSplit, SIZE_CLASSES, SIZE_LABEL, type SizeClass } from "../../lib/sizeClass";
import type { BreedRanking, Dog } from "../../lib/types";

const CURRENT_YEAR = new Date().getFullYear();
// "lifetime" sits at the top of the picker — AKC's all-time leaderboard for the breed.
// AKC Fast CAT started in 2016, so we extend back to that year dynamically.
const EARLIEST_YEAR = 2016;
const YEARS = ["lifetime", ...Array.from({ length: CURRENT_YEAR - EARLIEST_YEAR + 1 }, (_, i) => String(CURRENT_YEAR - i))];
const YEAR_LABEL = (y: string) => (y === "lifetime" ? "Lifetime" : y);

export default function RankingsScreen() {
  const params = useLocalSearchParams<{ breed?: string; year?: string; size?: string }>();
  const router = useRouter();

  const [breed, setBreed] = useState("");
  const [breedQuery, setBreedQuery] = useState("");
  const [showBreedList, setShowBreedList] = useState(false);
  const [year, setYear] = useState(String(CURRENT_YEAR));
  const [showYearList, setShowYearList] = useState(false);
  const [sizeClass, setSizeClass] = useState<SizeClass>("12_to_18");
  const [results, setResults] = useState<BreedRanking[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  // User's own dogs (if logged in) — used to highlight their entries in the rankings list.
  // Silently empty for logged-out users.
  const [myDogs, setMyDogs] = useState<Dog[]>([]);
  useEffect(() => {
    getDogs().then(setMyDogs).catch(() => setMyDogs([]));
  }, []);

  // Normalize names for substring matching. AKC suffixes earned titles to dog
  // names ("Banjo River Run FDC FCAT2"), so we match the user's registered name
  // as a substring of the ranked name.
  const normalize = (s: string) => s.toLowerCase().replace(/[‘’]/g, "'").replace(/\s+/g, " ").trim();
  const myRegNames = useMemo(
    () => myDogs.map((d) => d.registeredName).filter((s): s is string => !!s).map(normalize),
    [myDogs],
  );
  const isMine = (rankedDogName: string) => {
    if (myRegNames.length === 0) return false;
    const n = normalize(rankedDogName);
    return myRegNames.some((reg) => n.includes(reg));
  };

  const breedHasSizes = hasSizeSplit(breed);

  const filteredBreeds = useMemo(() => {
    if (!breedQuery.trim()) return AKC_BREEDS as unknown as string[];
    const q = breedQuery.toLowerCase();
    return (AKC_BREEDS as unknown as string[]).filter((b) => b.toLowerCase().includes(q));
  }, [breedQuery]);

  async function runSearch(b: string, y: string, s: SizeClass) {
    setLoading(true);
    setError("");
    setSearched(true);
    try {
      const data = await getRankings(
        b || undefined,
        y.trim() || undefined,
        hasSizeSplit(b) ? s : undefined,
      );
      setResults(data);
    } catch (err: any) {
      setError(err.message || "Failed to load rankings");
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch() {
    if (!breed) {
      setError("Please select a breed");
      return;
    }
    await runSearch(breed, year, sizeClass);
  }

  // Deep-link support: email CTAs pass ?breed=...&year=...&size=... and we
  // auto-apply + auto-search so the user lands on their filtered rankings.
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (didAutoSearch.current) return;
    const qBreed = typeof params.breed === "string" ? params.breed : "";
    if (!qBreed) return;
    didAutoSearch.current = true;
    const qYear = typeof params.year === "string" && params.year ? params.year : String(CURRENT_YEAR);
    const qSizeRaw = typeof params.size === "string" ? params.size : "";
    const qSize: SizeClass = (SIZE_CLASSES as readonly string[]).includes(qSizeRaw)
      ? (qSizeRaw as SizeClass)
      : "12_to_18";
    setBreed(qBreed);
    setBreedQuery(qBreed);
    setYear(qYear);
    setSizeClass(qSize);
    runSearch(qBreed, qYear, qSize);
  }, [params.breed, params.year, params.size]);

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Search bar */}
      <View
        style={{
          backgroundColor: "#ffffff",
          padding: 16,
          borderBottomWidth: 1,
          borderBottomColor: "#e5e7eb",
          gap: 10,
          zIndex: 10,
        }}
      >
        {/* Breed dropdown */}
        <View style={{ zIndex: 20 }}>
          <TouchableOpacity
            onPress={() => { setShowBreedList(!showBreedList); setShowYearList(false); }}
            style={{
              flexDirection: "row",
              alignItems: "center",
              backgroundColor: "#f3f4f6",
              borderRadius: 10,
              paddingHorizontal: 12,
              paddingVertical: 10,
              borderWidth: showBreedList ? 1 : 0,
              borderColor: "#1B2A4A",
            }}
          >
            <Ionicons name="paw-outline" size={16} color="#6b7280" />
            <Text
              style={{
                flex: 1,
                marginLeft: 8,
                fontSize: 16,
                color: breed ? "#111827" : "#9ca3af",
              }}
            >
              {breed || "Select breed..."}
            </Text>
            {breed ? (
              <TouchableOpacity
                onPress={() => {
                  setBreed("");
                  setBreedQuery("");
                  setSearched(false);
                  setResults([]);
                }}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close-circle" size={18} color="#9ca3af" />
              </TouchableOpacity>
            ) : (
              <Text style={{ color: "#6b7280", fontSize: 12 }}>{showBreedList ? "▲" : "▼"}</Text>
            )}
          </TouchableOpacity>

          {showBreedList && (
            <View
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                borderWidth: 1,
                borderColor: "#d1d5db",
                borderRadius: 10,
                backgroundColor: "#fff",
                marginTop: 4,
                maxHeight: 280,
                overflow: "hidden",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.1,
                shadowRadius: 8,
                elevation: 5,
              }}
            >
              <TextInput
                value={breedQuery}
                onChangeText={setBreedQuery}
                placeholder="Search breeds..."
                autoFocus
                style={{
                  borderBottomWidth: 1,
                  borderBottomColor: "#e5e7eb",
                  paddingVertical: 10,
                  paddingHorizontal: 14,
                  fontSize: 16,
                  color: "#111827",
                }}
                placeholderTextColor="#9ca3af"
              />
              <FlatList
                data={filteredBreeds}
                keyExtractor={(item) => item}
                style={{ maxHeight: 230 }}
                keyboardShouldPersistTaps="handled"
                renderItem={({ item }) => (
                  <TouchableOpacity
                    onPress={() => {
                      setBreed(item);
                      setBreedQuery("");
                      setShowBreedList(false);
                      // After the user's first explicit Search, switching breeds
                      // should auto-fire a new fetch — they already told us they
                      // wanted to browse, no reason to make them tap Search again.
                      if (searched) runSearch(item, year, sizeClass);
                    }}
                    style={{
                      paddingVertical: 11,
                      paddingHorizontal: 14,
                      backgroundColor: item === breed ? "#eff6ff" : "#fff",
                      borderBottomWidth: 1,
                      borderBottomColor: "#f3f4f6",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 16,
                        color: item === breed ? "#2563eb" : "#111827",
                        fontWeight: item === breed ? "600" : "400",
                      }}
                    >
                      {item}
                    </Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}
        </View>

        <View style={{ flexDirection: "row", gap: 10 }}>
          {/* Year dropdown */}
          <View style={{ flex: 1, zIndex: 5 }}>
            <TouchableOpacity
              onPress={() => { setShowYearList(!showYearList); setShowBreedList(false); }}
              style={{
                flexDirection: "row",
                alignItems: "center",
                backgroundColor: "#f3f4f6",
                borderRadius: 10,
                paddingHorizontal: 12,
                paddingVertical: 10,
                borderWidth: showYearList ? 1 : 0,
                borderColor: "#1B2A4A",
              }}
            >
              <Ionicons name="calendar-outline" size={16} color="#6b7280" />
              <Text style={{ flex: 1, marginLeft: 8, fontSize: 16, color: "#111827" }}>{YEAR_LABEL(year)}</Text>
              <Text style={{ color: "#6b7280", fontSize: 12 }}>{showYearList ? "▲" : "▼"}</Text>
            </TouchableOpacity>

            {showYearList && (
              <View
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  borderWidth: 1,
                  borderColor: "#d1d5db",
                  borderRadius: 10,
                  backgroundColor: "#fff",
                  marginTop: 4,
                  maxHeight: 200,
                  overflow: "hidden",
                  shadowColor: "#000",
                  shadowOffset: { width: 0, height: 4 },
                  shadowOpacity: 0.1,
                  shadowRadius: 8,
                  elevation: 5,
                }}
              >
                <FlatList
                  data={YEARS}
                  keyExtractor={(item) => item}
                  keyboardShouldPersistTaps="handled"
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      onPress={() => {
                        setYear(item);
                        setShowYearList(false);
                        if (searched && breed) runSearch(breed, item, sizeClass);
                      }}
                      style={{
                        paddingVertical: 11,
                        paddingHorizontal: 14,
                        backgroundColor: item === year ? "#eff6ff" : "#fff",
                        borderBottomWidth: 1,
                        borderBottomColor: "#f3f4f6",
                      }}
                    >
                      <Text style={{ fontSize: 16, color: item === year ? "#2563eb" : "#111827", fontWeight: item === year ? "600" : "400" }}>{YEAR_LABEL(item)}</Text>
                    </TouchableOpacity>
                  )}
                />
              </View>
            )}
          </View>

          <TouchableOpacity
            onPress={handleSearch}
            disabled={loading || !breed}
            style={{
              backgroundColor: "#1B2A4A",
              paddingHorizontal: 20,
              paddingVertical: 10,
              borderRadius: 10,
              justifyContent: "center",
              opacity: loading || !breed ? 0.5 : 1,
            }}
          >
            <Text style={{ color: "#ffffff", fontWeight: "600", fontSize: 14 }}>Search</Text>
          </TouchableOpacity>
        </View>

        {/* Size class toggle — only for variety breeds (Dachshund, Poodle, All American, etc.) */}
        {breedHasSizes && (
          <View style={{ marginTop: 10, flexDirection: "row", backgroundColor: "#f3f4f6", borderRadius: 10, padding: 4, gap: 2 }}>
            {SIZE_CLASSES.map((sc) => {
              const active = sc === sizeClass;
              return (
                <TouchableOpacity
                  key={sc}
                  onPress={() => {
                    setSizeClass(sc);
                    if (searched && breed) runSearch(breed, year, sc);
                  }}
                  style={{
                    flex: 1,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: active ? "#1B2A4A" : "transparent",
                    alignItems: "center",
                  }}
                >
                  <Text style={{ color: active ? "#ffffff" : "#4b5563", fontWeight: "600", fontSize: 13 }}>{SIZE_LABEL[sc]}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>

      <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 60 }}>
        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {loading ? (
          <View style={{ alignItems: "center", paddingVertical: 60 }}>
            <ActivityIndicator size="large" color="#2563eb" />
            <Text style={{ color: "#6b7280", marginTop: 12 }}>Loading rankings...</Text>
          </View>
        ) : !searched ? (
          <View style={{ alignItems: "center", paddingVertical: 60 }}>
            <Ionicons name="trophy-outline" size={56} color="#d1d5db" />
            <Text style={{ fontSize: 16, fontWeight: "600", color: "#374151", marginTop: 16 }}>
              Search for Rankings
            </Text>
            <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 6, textAlign: "center" }}>
              Enter a breed and year to see top rankings
            </Text>
          </View>
        ) : results.length === 0 ? (
          <View style={{ alignItems: "center", paddingVertical: 60 }}>
            <Text style={{ fontSize: 16, fontWeight: "600", color: "#374151" }}>No results found</Text>
            <Text style={{ fontSize: 14, color: "#6b7280", marginTop: 6 }}>
              Try a different breed or year
            </Text>
          </View>
        ) : (
          <View>
            <Text style={{ fontSize: 15, color: "#6b7280", marginBottom: 12 }}>
              {results.length} result{results.length !== 1 ? "s" : ""}
              {breed ? ` for ${breed}` : ""}
              {year ? ` (${YEAR_LABEL(year)})` : ""}
            </Text>

            {/* Results header */}
            <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <Text style={{ fontSize: 18, fontWeight: "800", color: "#1B2A4A" }}>{breed}</Text>
              <View style={{ backgroundColor: "#1B2A4A", paddingHorizontal: 10, paddingVertical: 4, borderRadius: 6 }}>
                <Text style={{ fontSize: 13, fontWeight: "700", color: "#fff" }}>{YEAR_LABEL(year)}</Text>
              </View>
            </View>

            {results.map((r) => {
              const isTop3 = r.rank <= 3;
              const isNationalsTier = r.rank === 4 || r.rank === 5; // Blue outline — top 5 invited to Nationals
              const mine = isMine(r.dogName);
              const isDash = !!r.publicSlug;
              const rankBg = r.rank === 1 ? "#F5A623"
                : r.rank === 2 ? "#94A3B8"
                : r.rank === 3 ? "#CD7F32"
                : isNationalsTier ? "#3B82F6" // blue for #4 and #5
                : "#1B2A4A";
              const hasFrame = isTop3 || isNationalsTier;
              const CardWrap: any = isDash ? TouchableOpacity : View;
              return (
                <CardWrap
                  key={r.id}
                  {...(isDash ? { activeOpacity: 0.85, onPress: () => router.push(`/dog/${r.publicSlug}?backTo=rankings&rBreed=${encodeURIComponent(breed)}&rYear=${encodeURIComponent(year)}&rSize=${sizeClass}` as any) } : {})}
                  style={{
                    backgroundColor: mine ? "#E0E7F2" : "#ffffff",
                    borderRadius: 12,
                    marginBottom: 8,
                    borderWidth: 1,
                    borderColor: hasFrame ? rankBg : "#e5e7eb",
                    borderLeftWidth: hasFrame ? 4 : 1,
                    borderLeftColor: hasFrame ? rankBg : "#e5e7eb",
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.04,
                    shadowRadius: 3,
                    elevation: 1,
                  }}
                >
                  {/* Top section — rank + name */}
                  <View style={{ flexDirection: "row", alignItems: "center", paddingHorizontal: 14, paddingTop: 12, paddingBottom: 8 }}>
                    {isTop3 ? (
                      <View style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center", marginRight: 12 }}>
                        <Text style={{ fontSize: 34 }}>{r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : "🥉"}</Text>
                      </View>
                    ) : isNationalsTier ? (
                      <View style={{ width: 44, height: 44, alignItems: "center", justifyContent: "center", marginRight: 12 }}>
                        <Ionicons name="ribbon" size={44} color={rankBg} style={{ position: "absolute", top: 0 }} />
                        {/* Solid circle overlay so the number sits on a clean background */}
                        <View
                          style={{
                            position: "absolute",
                            top: 5,
                            width: 20,
                            height: 20,
                            borderRadius: 10,
                            backgroundColor: rankBg,
                            alignItems: "center",
                            justifyContent: "center",
                            borderWidth: 1.5,
                            borderColor: "#ffffff",
                          }}
                        >
                          <Text style={{ fontSize: 11, fontWeight: "900", color: "#ffffff" }}>
                            {r.rank}
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 10,
                          backgroundColor: rankBg,
                          alignItems: "center",
                          justifyContent: "center",
                          marginRight: 12,
                        }}
                      >
                        <Text style={{ fontSize: 16, fontWeight: "900", color: "#ffffff" }}>
                          {r.rank}
                        </Text>
                      </View>
                    )}
                    <View style={{ flex: 1, flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      <Text style={{ flex: 1, fontSize: 15, fontWeight: "700", color: "#1B2A4A", lineHeight: 20 }} numberOfLines={2}>
                        {r.dogName}
                      </Text>
                      {mine && (
                        <View style={{ backgroundColor: "#1B2A4A", paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 }}>
                          <Text style={{ fontSize: 9, fontWeight: "800", color: "#ffffff" }}>YOU</Text>
                        </View>
                      )}
                      {isDash && !mine && (
                        <View style={{ backgroundColor: "#F5A623", paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4 }}>
                          <Text style={{ fontSize: 9, fontWeight: "800", color: "#1B2A4A" }}>🐾 ON DASH-HOUND</Text>
                        </View>
                      )}
                    </View>
                  </View>
                  {/* Bottom bar — stats: sex on left, time + mph grouped on right */}
                  <View
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      backgroundColor: "#f9fafb",
                      borderTopWidth: 1,
                      borderTopColor: "#f3f4f6",
                      paddingVertical: 8,
                      paddingHorizontal: 14,
                      borderBottomLeftRadius: 11,
                      borderBottomRightRadius: 11,
                    }}
                  >
                    <View style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: 6 }}>
                      <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: r.owner === "Dog" ? "#3B82F6" : r.owner === "Bitch" ? "#EC4899" : "#d1d5db" }} />
                      <Text style={{ fontSize: 13, color: "#6b7280", fontWeight: "500" }}>{r.owner || "—"}</Text>
                    </View>
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                        <Ionicons name="stopwatch-outline" size={14} color="#6b7280" />
                        <Text style={{ fontSize: 14, fontWeight: "700", color: "#374151" }}>
                          {r.speed != null && r.speed > 0 ? (204.545 / (r.speed as number)).toFixed(2) : "—"}
                        </Text>
                        <Text style={{ fontSize: 11, color: "#9ca3af", fontWeight: "500" }}>sec/avg</Text>
                      </View>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                        <Ionicons name="speedometer-outline" size={14} color="#F5A623" />
                        <Text style={{ fontSize: 15, fontWeight: "800", color: "#F5A623" }}>
                          {r.speed != null ? r.speed.toFixed(2) : "—"}
                        </Text>
                        <Text style={{ fontSize: 11, color: "#9ca3af", fontWeight: "500" }}>mph</Text>
                      </View>
                    </View>
                  </View>
                </CardWrap>
              );
            })}
          </View>
        )}
      </ScrollView>
    </SafeAreaView>
  );
}
